import React from 'react';
import { Check, X } from 'lucide-react';
import Tip from './Tip';

export type StatusValue = 'ACTIVE' | 'INACTIVE';

interface StatusSwitchProps {
  /** Current status. ON (green check) = ACTIVE, OFF (grey cross) = INACTIVE. */
  value: StatusValue;
  onChange: (next: StatusValue) => void;
  /** Noun shown in the hover tooltip, e.g. "users", "geography types". */
  noun?: string;
  /** Override the data-testid (defaults to BTN_status_toggle). */
  testId?: string;
  /** Tooltip placement. Defaults to 'top'; use 'right' when the switch sits at a container's left edge. */
  tipPlacement?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * iOS-style on/off switch for the standard Active/Inactive list filter.
 * Drop-in replacement for the old "All Active" status dropdown.
 * ON = ACTIVE (green, check) · OFF = INACTIVE (grey, cross).
 */
const StatusSwitch: React.FC<StatusSwitchProps> = ({
  value,
  onChange,
  noun = 'records',
  testId = 'BTN_status_toggle',
  tipPlacement = 'top',
}) => {
  const on = value === 'ACTIVE';
  return (
    <Tip text={on ? `Showing active ${noun}` : `Showing inactive ${noun}`} placement={tipPlacement}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`Show active ${noun}`}
        data-testid={testId}
        className={`ios-switch${on ? ' ios-switch--on' : ''}`}
        onClick={() => onChange(on ? 'INACTIVE' : 'ACTIVE')}
      >
        <span className="ios-switch-knob" aria-hidden="true">
          {on ? <Check size={10} strokeWidth={3} /> : <X size={10} strokeWidth={3} />}
        </span>
      </button>
    </Tip>
  );
};

export default StatusSwitch;
